// OurEvenPage.jsx
import React, {useState, useEffect} from 'react';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    Alert,
    Chip,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Snackbar,
    Tooltip,
    Stack,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {format} from 'date-fns';
import {getAllOurEvents, createOurEvent, deleteOurEvent, updateOurEvent} from "../api/AiAgentApi";
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Компонент страницы
export default function OurEvenPage() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentEventId, setCurrentEventId] = useState(null);
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});
    const [deleteDialog, setDeleteDialog] = useState({open: false, eventId: null, eventName: ''});
    const [newEvent, setNewEvent] = useState({
        name: '',
        description: '',
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm")
    });

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            setLoading(true);
            const data = await getAllOurEvents();
            setEvents(data);
            setError(null);
        } catch (err) {
            setError('Не удалось загрузить мероприятия. Пожалуйста, попробуйте позже.');
            console.error('Error loading events:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
        } catch {
            return dateString;
        }
    };

    // Добавление мероприятия
    const handleOpenAddDialog = () => {
        setIsEditMode(false);
        setNewEvent({
            name: '',
            description: '',
            date: format(new Date(), "yyyy-MM-dd'T'HH:mm")
        });
        setOpenDialog(true);
    };

    // Редактирование мероприятия
    const handleOpenEditDialog = (event) => {
        setIsEditMode(true);
        setCurrentEventId(event.id);
        setNewEvent({
            name: event.name,
            description: event.description,
            date: format(new Date(event.date || event.createdAt), "yyyy-MM-dd'T'HH:mm")
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setIsEditMode(false);
        setCurrentEventId(null);
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setNewEvent(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            // Валидация
            if (!newEvent.name.trim()) {
                setSnackbar({
                    open: true,
                    message: 'Пожалуйста, введите название мероприятия',
                    severity: 'error'
                });
                return;
            }

            if (!newEvent.description.trim()) {
                setSnackbar({
                    open: true,
                    message: 'Пожалуйста, введите описание мероприятия',
                    severity: 'error'
                });
                return;
            }

            // Форматируем дату для API
            const eventData = {
                ...newEvent,
                date: new Date(newEvent.date).toISOString()
            };

            if (isEditMode) {
                // Обновляем мероприятие
                await updateOurEvent(currentEventId, eventData);
                setSnackbar({
                    open: true,
                    message: 'Мероприятие успешно обновлено!',
                    severity: 'success'
                });
            } else {
                // Создаем новое мероприятие
                await createOurEvent(eventData);
                setSnackbar({
                    open: true,
                    message: 'Мероприятие успешно добавлено!',
                    severity: 'success'
                });
            }

            // Закрываем диалог и обновляем список
            handleCloseDialog();
            await loadEvents();

        } catch (err) {
            console.error('Error saving event:', err);
            const action = isEditMode ? 'обновлении' : 'добавлении';
            setSnackbar({
                open: true,
                message: `Ошибка при ${action} мероприятия. Пожалуйста, попробуйте еще раз.`,
                severity: 'error'
            });
        }
    };

    // Удаление мероприятия
    const handleOpenDeleteDialog = (eventId, eventName) => {
        setDeleteDialog({
            open: true,
            eventId,
            eventName
        });
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog({
            open: false,
            eventId: null,
            eventName: ''
        });
    };

    const handleDeleteEvent = async () => {
        try {
            await deleteOurEvent(deleteDialog.eventId);

            // Закрываем диалог и показываем уведомление
            handleCloseDeleteDialog();
            setSnackbar({
                open: true,
                message: 'Мероприятие успешно удалено!',
                severity: 'success'
            });

            // Обновляем список мероприятий
            await loadEvents();

        } catch (err) {
            console.error('Error deleting event:', err);
            setSnackbar({
                open: true,
                message: 'Ошибка при удалении мероприятия. Пожалуйста, попробуйте еще раз.',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({...snackbar, open: false});
    };

    return (
        <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
            {/* Заголовок и кнопки управления */}
            <Box sx={{mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Наши Мероприятия
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Список всех запланированных мероприятий
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <Tooltip title="Обновить список">
                        <IconButton onClick={loadEvents} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Добавить мероприятие
                    </Button>
                </Stack>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 3}}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress/>
                </Box>
            ) : events.length === 0 ? (
                <Paper sx={{p: 6, textAlign: 'center'}}>
                    <EventIcon sx={{fontSize: 60, color: 'text.secondary', mb: 2}} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Мероприятия не найдены
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                        Добавьте первое мероприятие, нажав на кнопку "Добавить мероприятие"
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Добавить мероприятие
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {/* Табличный вид */}
                    <Grid item xs={12}>
                        <Paper sx={{width: '100%', overflow: 'hidden', mt: 2}}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Название</TableCell>
                                            <TableCell>Описание</TableCell>
                                            <TableCell>Статус</TableCell>
                                            <TableCell>Дата создания</TableCell>
                                            <TableCell align="center">Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {events.map((event) => (
                                            <TableRow key={event.id} hover>
                                                <TableCell>{event.id}</TableCell>
                                                <TableCell>
                                                    <Typography fontWeight="medium">
                                                        {event.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            maxWidth: 300,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical'
                                                        }}
                                                    >
                                                        {event.description}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={event.isActive ? "Активно" : "Неактивно"}
                                                        color={event.isActive ? "success" : "error"}
                                                        size="small"
                                                        variant={event.isActive ? "filled" : "outlined"}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(event.createdAt)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <EventActionsMenu
                                                        event={event}
                                                        onEdit={handleOpenEditDialog}
                                                        onDelete={handleOpenDeleteDialog}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Диалог добавления/редактирования мероприятия */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {isEditMode ? 'Редактировать мероприятие' : 'Добавить новое мероприятие'}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{mt: 2}}>
                        <TextField
                            fullWidth
                            label="Название мероприятия"
                            name="name"
                            value={newEvent.name}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            autoFocus
                        />
                        <TextField
                            fullWidth
                            label="Описание"
                            name="description"
                            value={newEvent.description}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            multiline
                            rows={4}
                        />
                        <TextField
                            fullWidth
                            label="Дата и время мероприятия"
                            name="date"
                            type="datetime-local"
                            value={newEvent.date}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {isEditMode ? 'Сохранить' : 'Добавить'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подтверждения удаления */}
            <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Подтверждение удаления</DialogTitle>
                <DialogContent>
                    <Typography>
                        Вы уверены, что хотите удалить мероприятие "{deleteDialog.eventName}"?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        Это действие нельзя отменить.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Отмена</Button>
                    <Button
                        onClick={handleDeleteEvent}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Снэкбар для уведомлений */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

// Меню действий для таблицы
function EventActionsMenu({ event, onEdit, onDelete }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        onEdit(event);
        handleClose();
    };

    const handleDelete = () => {
        onDelete(event.id, event.name);
        handleClose();
    };

    return (
        <>
            <IconButton
                aria-label="Действия"
                onClick={handleClick}
                size="small"
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Редактировать</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>Удалить</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}